import { supabase } from '@/integrations/supabase/client';

/**
 * Migrates user data from localStorage to database tables
 * Should be called once per user after login
 */
export async function migrateLocalStorageToDatabase(userId: string): Promise<void> {
  if (!userId) return;

  const migrationKey = `migration_completed_${userId}`;
  if (localStorage.getItem(migrationKey)) {
    return; // Already migrated
  }

  try {
    // Migrate favorites
    const favoritesKey = `favorites_${userId}`;
    const favoritesData = localStorage.getItem(favoritesKey);
    if (favoritesData) {
      try {
        const favorites = JSON.parse(favoritesData);
        if (Array.isArray(favorites)) {
          const favoriteRecords = favorites.map(fav => ({
            user_id: userId,
            item_id: fav.id || fav.item_id,
            item_type: fav.type || fav.item_type || 'course',
            metadata: fav,
          }));
          
          await supabase.from('user_favorites').upsert(favoriteRecords);
          localStorage.removeItem(favoritesKey);
          console.log('Migrated favorites to database');
        }
      } catch (error) {
        console.error('Failed to migrate favorites:', error);
      }
    }

    // Migrate bookmarks (audio-bookmarks pattern)
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith('audio-bookmarks-')) {
        const moduleId = key.replace('audio-bookmarks-', '');
        const bookmarkData = localStorage.getItem(key);
        
        if (bookmarkData) {
          try {
            const bookmark = JSON.parse(bookmarkData);
            await supabase.from('user_bookmarks').upsert({
              user_id: userId,
              module_id: moduleId,
              course_id: bookmark.courseId || bookmark.course_id || '',
              position: bookmark.position || bookmark.time || 0,
              notes: bookmark.notes,
            });
            localStorage.removeItem(key);
            console.log(`Migrated bookmark for module ${moduleId}`);
          } catch (error) {
            console.error(`Failed to migrate bookmark ${key}:`, error);
          }
        }
      }
    }

    // Migrate notes (audio_notes pattern)
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(`audio_notes_${userId}_`)) {
        const moduleId = key.replace(`audio_notes_${userId}_`, '');
        const notesData = localStorage.getItem(key);
        
        if (notesData) {
          try {
            const notes = JSON.parse(notesData);
            if (Array.isArray(notes)) {
              const noteRecords = notes.map(note => ({
                user_id: userId,
                module_id: moduleId,
                course_id: note.courseId || note.course_id || '',
                note_content: note.content || note.text || '',
                timestamp: note.timestamp,
              }));
              
              await supabase.from('user_notes').upsert(noteRecords);
              localStorage.removeItem(key);
              console.log(`Migrated notes for module ${moduleId}`);
            }
          } catch (error) {
            console.error(`Failed to migrate notes ${key}:`, error);
          }
        }
      }
    }

    // Migrate preferences
    const preferencesKeys = ['theme', 'accessibility_settings', 'audio_settings'];
    const preferences: any = { user_id: userId };
    let hasPreferences = false;

    preferencesKeys.forEach(key => {
      const value = localStorage.getItem(key);
      if (value) {
        try {
          preferences[key] = JSON.parse(value);
          hasPreferences = true;
        } catch {
          preferences[key] = value;
          hasPreferences = true;
        }
      }
    });

    if (hasPreferences) {
      await supabase.from('user_preferences').upsert(preferences);
      preferencesKeys.forEach(key => localStorage.removeItem(key));
      console.log('Migrated user preferences');
    }

    // Mark migration as complete
    localStorage.setItem(migrationKey, 'true');
    console.log('localStorage migration completed successfully');
  } catch (error) {
    console.error('Failed to complete localStorage migration:', error);
  }
}
